import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  type ButtonProps,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { OrganisationType } from "../../../../clients/graphqlTypes.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import {
  ONBOARDING_BUTTON_HEIGHT,
  ONBOARDING_BUTTON_LABEL,
  ONBOARDING_COLORS,
  ONBOARDING_CONTENT_WIDTH,
  ONBOARDING_GUTTER,
} from "./onboardingDesign.js";
import { getOnboardingStepProgress } from "./onboardingFlowSteps.js";

export interface OnboardingStageAction {
  label: string;
  handleClick?: () => void;
  buttonProps?: Pick<ButtonProps, "id" | "type" | "form">;
  /** A save is in flight: the button shows a spinner instead of being disabled. */
  loading?: boolean;
  /** Becomes the button's `data-testid`, which end-to-end tests select by. */
  testId?: string;
}

interface Props {
  stage: OnboardingFlowStage;
  organisationType: OrganisationType;
  title: string;
  description?: string;
  /** Shown left of the footer buttons, e.g. "2 selected". */
  summary?: React.ReactNode;
  /** Space between the heading block and the content (design: 35px, the trades step 28px). */
  contentSpacing?: string;
  /** A tighter top padding on desktop (the trades step: 40px instead of 56px). */
  compactTop?: boolean;
  skip?: OnboardingStageAction;
  primary: OnboardingStageAction;
  children: React.ReactNode;
}

/**
 * The content column of a wizard step: title and description over
 * the stage's content, with the SKIP / CONTINUE bar pinned to the bottom while
 * the content scrolls. CONTINUE is never disabled: a stage validates on click
 * and reports what is missing. The rail already shows the step list; only on a
 * phone, where the rail's list is hidden, a compact "Step N of M" line stays.
 */
export const OnboardingStageLayout = ({
  stage,
  organisationType,
  title,
  description,
  summary,
  contentSpacing = "35px",
  compactTop = false,
  skip,
  primary,
  children,
}: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const progress = getOnboardingStepProgress(stage, organisationType);
  const stepLabel = progress
    ? t("Step {current} of {total}", {
        ns: "OrganisationProfile",
        current: progress.current,
        total: progress.total,
      })
    : null;
  const columnWidth = ONBOARDING_CONTENT_WIDTH;

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // A step shorter than the screen is centred vertically: the column's
          // auto margins take the free space, and give it back (top-aligned,
          // scrolling) once the content is taller.
          display: "flex",
          flexDirection: "column",
          px: ONBOARDING_GUTTER,
          pt: { xs: 3, md: compactTop ? "40px" : "56px" },
          pb: "32px",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: columnWidth, m: "auto" }}>
          <Stack spacing={contentSpacing}>
            <Stack spacing="12px">
              <Stack sx={{ pt: { md: "8px" } }}>
                {stepLabel && (
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      mb: "10px",
                      fontSize: 12,
                      lineHeight: 1.5,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: ONBOARDING_COLORS.body,
                    }}
                  >
                    {stepLabel}
                  </Typography>
                )}
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 26, md: 32 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                    color: ONBOARDING_COLORS.heading,
                  }}
                >
                  {title}
                </Typography>
              </Stack>
              {description && (
                <Typography
                  sx={{
                    maxWidth: 680,
                    fontSize: 16,
                    lineHeight: 1.55,
                    letterSpacing: "normal",
                    color: ONBOARDING_COLORS.body,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Stack>
            {children}
          </Stack>
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          px: ONBOARDING_GUTTER,
          py: "20px",
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {/* On a phone both actions are full width, the primary one first and
            Skip below it; from `sm` on both sit on one row, right-aligned. */}
        <Stack
          direction="row"
          sx={{
            width: "100%",
            maxWidth: columnWidth,
            mx: "auto",
            alignItems: "center",
            justifyContent: "flex-end",
            // A long summary wraps inside the row on desktop instead of pushing
            // the buttons to a second row.
            flexWrap: { xs: "wrap", sm: "nowrap" },
            columnGap: "8px",
            rowGap: { xs: 1, sm: 0.5 },
          }}
        >
          {summary && (
            <Typography
              component="div"
              sx={{
                order: { xs: 0, sm: 1 },
                flexBasis: { xs: "100%", sm: "auto" },
                mr: { sm: 1 },
                fontSize: 13,
                lineHeight: 1.45,
                letterSpacing: "normal",
                color: ONBOARDING_COLORS.muted,
                textAlign: { xs: "left", sm: "right" },
              }}
            >
              {summary}
            </Typography>
          )}
          {skip && (
            <Button
              variant="outlined"
              color="primary"
              onClick={skip.handleClick}
              data-testid={skip.testId}
              sx={{
                order: { xs: 3, sm: 2 },
                flexBasis: { xs: "100%", sm: "auto" },
                flexShrink: 0,
                bgcolor: "background.paper",
                height: ONBOARDING_BUTTON_HEIGHT,
                px: "22px",
                color: ONBOARDING_COLORS.dsNavy,
                ...ONBOARDING_BUTTON_LABEL,
              }}
              {...skip.buttonProps}
            >
              {skip.label}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={primary.handleClick}
            loading={primary.loading}
            data-testid={primary.testId}
            sx={{
              order: { xs: 1, sm: 3 },
              flexBasis: { xs: "100%", sm: "auto" },
              flexShrink: 0,
              height: ONBOARDING_BUTTON_HEIGHT,
              px: "22px",
              ...ONBOARDING_BUTTON_LABEL,
            }}
            {...primary.buttonProps}
          >
            {primary.label}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
